import React from "react";

import flattenChildren from "react-keyed-flatten-children";

import {
  Display,
  JustifyContent,
  ResponsiveDimension,
  ResponsiveProp
} from "../../../types";
import { normaliseResponsiveProp } from "../../../utils/responsiveProps";
import Box from "../Box";

type Align = "start" | "end" | "center";

type Props = {
  children: React.ReactNode;
  space?: ResponsiveDimension;
  align?: ResponsiveProp<Align>;
};

const Stack = React.forwardRef<any, Props>(
  ({ children, space, align = "start" }, ref) => {
    return (
      <Box ref={ref} display="flex" flexDirection="column" width="100%">
        {React.Children.map(flattenChildren(children), (child, index) => (
          <Box
            key={index}
            {...getChildStyle({
              space,
              index,
              align
            })}
            width="100%"
          >
            {child}
          </Box>
        ))}
      </Box>
    );
  }
);

type Args = {
  align?: ResponsiveProp<Align>;
  space?: ResponsiveDimension;
  index: number;
};

function getChildStyle({ space, align, index }: Args) {
  const normalisedAlign = normaliseResponsiveProp(align);
  const { display, justifyContent } = normalisedAlign.reduce<{
    display: [Display, Display, Display];
    justifyContent: [JustifyContent, JustifyContent, JustifyContent];
  }>(
    (reduction, align) => {
      return {
        display: ([...reduction.display, resolveDisplay(align)] as unknown) as [
          Display,
          Display,
          Display
        ],
        justifyContent: ([
          ...reduction.justifyContent,
          resolveJustifyContent(align)
        ] as unknown) as [JustifyContent, JustifyContent, JustifyContent]
      };
    },
    {
      display: ([] as unknown) as [Display, Display, Display],
      justifyContent: ([] as unknown) as [
        JustifyContent,
        JustifyContent,
        JustifyContent
      ]
    }
  );

  if (index === 0) {
    return { display, justifyContent };
  }

  return { marginTop: space, display, justifyContent };
}

function resolveDisplay(align?: Align) {
  if (align === "end") {
    return "flex";
  }

  if (align === "center") {
    return "flex";
  }

  return "block";
}

function resolveJustifyContent(align?: Align) {
  if (align === "end") {
    return "flex-end";
  }

  if (align === "center") {
    return "center";
  }

  return "flex-start";
}

export default Stack;
